<script setup lang="ts">
import { mdiDiamondStone, mdiBookshelf, mdiEyeOff, mdiEye, mdiAccountGroup } from '@mdi/js'
import type { GrimoireItem } from '~/types/interfaces'
import type { Work, CoverImageKeys } from '~/composables/api/useWorkApi'
import type { GithubRepo } from '~/composables/api/useGithubApi'
// #719 観点B/D: モバイルナビ背景に敷くゆりかごのマップ画像。
import cradleMapBg from '@/assets/images/map/AreaMap_CradleOfFeatherQuill.webp'
// #719 観点D: モバイル書庫カードの本の表紙（type→色は GrimoireCardGrid と同じ）。
import greenCodex from '@/assets/images/GreenCodex_256x256.webp'
import blueCodex from '@/assets/images/BlueCodex_256x256.webp'
import brownCodex from '@/assets/images/BrownCodex_256x256.webp'
import redCodex from '@/assets/images/RedCodex_256x256.webp'
import blackCodex from '@/assets/images/BlackCodex_256x256.webp'

const runtimeConfig = useRuntimeConfig()

// #719 観点B/D: モバイルは「読む・遊ぶ」のみ（写経・編集は出さない）。
const { isMobile } = useIsMobile()

useSeoMeta({
  title: '羽根筆のゆりかご | Code & Magic',
  description: '自分だけの魔法を作り、クリスタル化して世界に公開できる、筆と机です。',
  ogTitle: '羽根筆のゆりかご | Code & Magic',
  ogDescription: '自分だけの魔法を作り、クリスタル化して世界に公開できる、筆と机です。',
  ogImage: `${runtimeConfig.public.serviceDomain}/images/ogPlayMakeShare_wLogo.webp`,
  twitterCard: 'summary_large_image',
})

definePageMeta({
  middleware: ['authenticated', 'confirm-game-state'],
  layout: false,
  usePageReady: true,
})

const api = useApi()
const { user: userInfo } = storeToRefs(useGameStateStore())
const { triggerEventAndProcess, processEventsSequentially } = useEventSystem()
const { reloadGameState } = useGameStateStore()
const { trackGrimoireCreate, trackCodeRun, trackCrystallize, trackWorkPlay } = useFunnelEvent()

// Tabs
const tab = ref<string>('vault')
const grimoireItems = ref<GrimoireItem[]>([])
const categoriesToShow = ref<string[]>(['user', 'encodia', 'privilege', 'crystal'])

// GitHub connection
const githubAvailable = ref(false)
const githubConnected = ref(false)
const githubUsername = ref<string | null>(null)
const githubDisconnectConfirm = ref(false)
const githubRepos = ref<GithubRepo[]>([])

const checkGithubConnection = async () => {
  // Child accounts cannot use GitHub integration (GitHub ToS requires age 13+)
  if (userInfo.value?.isChild) {
    githubAvailable.value = false
    return
  }
  try {
    const res = await api.getGithubAuthUrl()
    githubAvailable.value = true
    githubConnected.value = res.connected
    githubUsername.value = res.githubUsername
  }
  catch {
    githubAvailable.value = false
  }
}

const loadGithubRepos = async () => {
  if (!githubConnected.value) return
  try {
    const res = await api.listGithubRepos()
    githubRepos.value = res.repos
  }
  catch (e) {
    console.error('Failed to load GitHub repos', e)
    githubRepos.value = []
  }
}

const githubGrimoireItems = computed<GrimoireItem[]>(() =>
  githubRepos.value.map(repo => ({
    id: `github:${repo.fullName}`,
    type: 'github',
    name: repo.name,
    readonly: true,
    description: repo.description ?? '',
    createdAt: repo.updatedAt,
  })),
)

const allGrimoireItems = computed<GrimoireItem[]>(() => [
  ...grimoireItems.value,
  ...githubGrimoireItems.value,
])

// #721: モバイル書庫のタイプ別フィルタ。魔導書が増えると探しづらいので、
// 実際に存在するタイプだけチップで出して絞り込めるようにする。
const GRIMOIRE_TYPE_LABEL: Record<string, string> = {
  user: '自作',
  encodia: 'エンコーディア',
  privilege: '導き',
  crystal: 'クリスタル',
  github: 'GitHub',
}
const vaultFilter = ref<string>('all')
const vaultTypeOptions = computed<string[]>(() => {
  const present = new Set(allGrimoireItems.value.map(i => i.type))
  return Object.keys(GRIMOIRE_TYPE_LABEL).filter(t => present.has(t))
})
const filteredGrimoireItems = computed<GrimoireItem[]>(() =>
  vaultFilter.value === 'all'
    ? allGrimoireItems.value
    : allGrimoireItems.value.filter(i => i.type === vaultFilter.value),
)

const githubConnectConfirm = ref(false)

const onGithubConnect = () => {
  githubConnectConfirm.value = true
}

const onConfirmGithubConnect = async () => {
  githubConnectConfirm.value = false
  try {
    const res = await api.getGithubAuthUrl()
    window.location.href = res.authorizationUrl
  }
  catch (e) {
    console.error('GitHub OAuth start failed', e)
  }
}

const onGithubDisconnect = () => {
  githubDisconnectConfirm.value = true
}

const onConfirmGithubDisconnect = async () => {
  try {
    await api.disconnectGithub()
    githubConnected.value = false
    githubUsername.value = null
    githubRepos.value = []
    categoriesToShow.value = categoriesToShow.value.filter(c => c !== 'github')
  }
  catch (e) {
    console.error('GitHub disconnect failed', e)
  }
  finally {
    githubDisconnectConfirm.value = false
  }
}

// Crystal tab — my published works
//
// #803: 以前は `listMyWorks()` を引数なしで呼んでおり、`per_page` の既定 50・
// 1 ページ目だけ＝**51 件目以降がサイレントに消えていた**（総数の表示も無いので
// 気づけない）。追記スクロールにして上限を外す。タブは無いので器（WorkGallery）は
// 使わず、振る舞いだけ `useWorkGallery` から借りて末尾に Loader を置く。
const MY_WORKS_PER_PAGE = 24
const {
  works: myWorks,
  hasMore: myWorksHasMore,
  loadingMore: myWorksLoadingMore,
  error: myWorksError,
  loadMore: loadMoreMyWorks,
  reload: loadMyWorks,
  retry: retryMyWorks,
} = useWorkGallery({
  fetcher: async (page, perPage) => {
    const res = await api.listMyWorks(page, perPage)
    return { works: res.works, total: res.total }
  },
  perPage: MY_WORKS_PER_PAGE,
})

// 冒険者のクリスタルタブ (#708) — 運営編集スタッフ (Dr.D) 専用。
// 全冒険者の公開クリスタルを ReadOnly で横断閲覧し、寸評執筆時にコードを読む。
const isEditorial = computed(() => userInfo.value?.isEditorial ?? false)
const adventurerCrystals = ref<GrimoireItem[]>([])
const adventurerCrystalsLoaded = ref(false)
const loadAdventurerCrystals = async () => {
  if (!isEditorial.value) return
  adventurerCrystals.value = (await api.listProjects('all_crystals')).projects.map(item => ({
    id: item.projectId,
    name: item.name,
    type: item.type,
    readonly: item.readonly,
    description: item.description,
    createdAt: item.createdAt,
    updatedAt: item.updatedAt,
  }))
  adventurerCrystalsLoaded.value = true
}

// スタッフが「冒険者のクリスタル」タブを初めて開いたときだけ遅延ロードする
// (一般ユーザーは API が空を返すうえタブ自体が非表示なので取得しない)。
watch(tab, (t) => {
  if (t === 'adventurers' && isEditorial.value && !adventurerCrystalsLoaded.value) {
    loadAdventurerCrystals()
  }
})

const onPlayWork = async (work: Work) => {
  const session = await api.createWorkSession(work.workId)
  trackWorkPlay(work.publicUrl.split('/').pop() ?? '', true)
  sessionUrl.value = session.sessionUrl
  sessionPackages.value = session.pyodidePackages ?? []
  // #752: この作品を PlayDialog に渡してお気に入り★を出す（windowed/ワイド共通）。
  currentPlayWork.value = work
  playDialog.value = true
}

const visibilityConfirmDialog = ref(false)
const visibilityTargetWork = ref<Work | null>(null)
const visibilityTargetValue = ref<'public' | 'private'>('private')

const onToggleVisibility = (work: Work) => {
  // 非公開から公開に戻すのも公開（#975）。子アカウントで保護者が OFF のときは知らせて止める
  if (work.visibility !== 'public' && userInfo.value?.canCrystallize === false) {
    alert('作品を公開するには、保護者の設定で「公開」を ON にしてもらいましょう。')
    return
  }
  visibilityTargetWork.value = work
  visibilityTargetValue.value = work.visibility === 'public' ? 'private' : 'public'
  visibilityConfirmDialog.value = true
}

const onConfirmVisibility = async () => {
  if (!visibilityTargetWork.value) return
  await api.updateWorkVisibility(visibilityTargetWork.value.workId, visibilityTargetValue.value)
  visibilityConfirmDialog.value = false
  visibilityTargetWork.value = null
  await loadMyWorks()
}

// Select Grimoire
const selectedGrimoire = ref<GrimoireItem | null>(null)

// 通常の魔導書 (vault) と冒険者のクリスタル (#708) の両方から引く。
const findGrimoireItem = (projectId: string): GrimoireItem | null =>
  allGrimoireItems.value.find((item: GrimoireItem) => item.id === projectId)
  ?? adventurerCrystals.value.find((item: GrimoireItem) => item.id === projectId)
  ?? null

// GrimoireCardGrid / GrimoireSelector は選択解除時に null を emit する。
const onSelectGrimoire = async (projectId: string | null) => {
  selectedGrimoire.value = projectId ? findGrimoireItem(projectId) : null
}

// Show detail dialog about the selected grimoire
const detailDialog = ref<boolean>(false)

// #743: 隠しリネージュビューア「この魔導書の系譜」。detail dialog 内の目立たない
// 1 ドットから開く（ungated だが backend の ProjectAccessService 経由アクセス制御に
// 委ねるため、UI 側では特に権限チェックしない）。
const lineageViewDialog = ref<boolean>(false)

// GitHub entry point dialog
const entryPointDialog = ref(false)
const entryPointRepoFullName = ref('')
const entryPointRepoName = ref('')

// GitHub README dialog
const readmeDialog = ref(false)
const readmeRepoFullName = ref('')
const readmeRepoName = ref('')

const onDetailGrimoire = async (projectId: string) => {
  console.log('onDetailGrimoire', projectId)
  selectedGrimoire.value = findGrimoireItem(projectId)

  // GitHub repos → open README dialog
  if (projectId.startsWith('github:')) {
    const fullName = projectId.replace('github:', '')
    readmeRepoFullName.value = fullName
    readmeRepoName.value = selectedGrimoire.value?.name ?? fullName
    readmeDialog.value = true
    return
  }

  detailDialog.value = true
}

// GitHub flow state
const githubPreviewSource = ref<{ owner: string, repo: string, entrypoint: string } | null>(null)
const githubPreviewSessionId = ref<string | null>(null)
const githubCompiling = ref(false)
const githubErrorMessage = ref<string | null>(null)

// GitHub play: entry point selection → play session
const onEntryPointSelected = async (payload: { repoFullName: string, entryPoint: string }) => {
  entryPointDialog.value = false
  githubCompiling.value = true

  const [owner, repo] = payload.repoFullName.split('/')
  githubPreviewSource.value = { owner: owner ?? '', repo: repo ?? '', entrypoint: payload.entryPoint }

  try {
    const session = await api.createGithubSession(owner ?? '', repo ?? '', payload.entryPoint)
    githubCompiling.value = false
    sessionUrl.value = session.sessionUrl
    sessionPackages.value = session.pyodidePackages ?? []
    // #752: GitHub 魔導書再生も Work ではないので★対象なし。
    currentPlayWork.value = null
    playDialog.value = true
  }
  catch (e: unknown) {
    githubCompiling.value = false
    githubPreviewSource.value = null
    console.error('GitHub session failed', e)
    if (showScreeningErrorIfApplicable(e)) {
      // AST 違反は magician の EventDialog で表示
      return
    }
    // #765: 「読み解けなかった」で全部を丸める前に、原因が分かるものは原因を出す
    // （未接続・リポ不明・レート制限は、次の一手がまったく違う）。
    githubErrorMessage.value = toUserMessage(
      e,
      'この魔導書はうまく読み解けなかったようじゃ。エントリーポイントが正しいか確認してくれ。',
    )
  }
}

// 各カードの主アクション。GrimoireSlot の固定ボタン (program / read / play)
// それぞれに対応する。詳細は doc/domain/cradle.md「カード操作のマトリックス」。
const playDialog = ref<boolean>(false)
const sessionUrl = ref<string>('')
// #663: Pyodide built-in packages (numpy etc.) returned from session creation.
const sessionPackages = ref<string[]>([])
// #752: 結晶タブから作品を再生しているときの Work（お気に入り★用）。魔導書
// （grimoire）再生では null。PlayDialog に渡すと windowed/ワイド両方で★が出る。
const currentPlayWork = ref<Work | null>(null)
// #722: 再生中の BGM 停止は PlayDialog 自身が担う（ページ側では制御しない）。

// プログラム！(編集可な user / cleared encodia) → Playground を編集モードで開く
const onProgramGrimoire = async (projectId: string) => {
  console.log('onProgramGrimoire', projectId)
  return navigateTo(`playground/${projectId}`)
}

// コードを読む (readonly な non-github) → Playground を viewer モードで開く
// (Playground 側が file.readonly に応じてエディタを read-only に切り替える)
const onReadGrimoire = async (projectId: string) => {
  console.log('onReadGrimoire', projectId)
  return navigateTo(`playground/${projectId}`)
}

// #719 観点D: モバイルは Playground（Monaco エディタ）ではなく read-only の
// 軽量ソースビューアで開く（写経・編集は出さない）。ビューアの「実行」は
// onPlayGrimoire に委譲し、同じ PlayDialog を没入で開く。
const mobileViewerOpen = ref(false)
const mobileViewerProjectId = ref('')
const mobileViewerTitle = ref('')

// 魔導書の description は MDC/HTML（<br> / <ruby> / <small> 等）を含みうるので、
// モバイルの1行プレビューではタグを落として素のテキストにする。
const plainDesc = (s: string): string =>
  (s || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()

// 書庫カードの本の表紙（type→色は GrimoireCardGrid.getGrimoireColor と同じ規則）。
const grimoireCover = (type: string): string => {
  switch (type) {
    case 'encodia':
      return greenCodex
    case 'crystal':
    case 'snapshot':
      return blueCodex
    case 'privilege':
      return brownCodex
    case 'github':
      return blackCodex
    case 'encodia_reference':
      return greenCodex // teal 表紙は無いので緑で代用
    default:
      return redCodex // user 等（白い魔導書＝赤表紙）
  }
}

const onOpenMobileGrimoire = (item: GrimoireItem) => {
  // GitHub 黒魔導書はソース取得経路が別なので、モバイルではそのまま実行導線へ。
  if (item.type === 'github') {
    onPlayGrimoire(item.id)
    return
  }
  mobileViewerProjectId.value = item.id
  mobileViewerTitle.value = item.name
  mobileViewerOpen.value = true
}

const onMobileViewerRun = (projectId: string) => {
  mobileViewerOpen.value = false
  onPlayGrimoire(projectId)
}

// 「くわしく見る」ダイアログの「選択する」ボタン用。type/readonly に応じて
// 主アクションを dispatch する。
const onOpenSelectedGrimoire = async () => {
  const item = selectedGrimoire.value
  if (!item) return
  if (item.type === 'github') return onPlayGrimoire(item.id)
  if (item.readonly) return onReadGrimoire(item.id)
  return onProgramGrimoire(item.id)
}

// 動かしてみる (readonly grimoire / GitHub black grimoire)
// GitHub は entrypoint ダイアログ経由、それ以外は createProjectSession 経由で
// PyxelPlayer を play dialog で起動する。
const onPlayGrimoire = async (projectId: string) => {
  console.log('onPlayGrimoire', projectId)
  if (projectId.startsWith('github:')) {
    const fullName = projectId.replace('github:', '')
    entryPointRepoFullName.value = fullName
    entryPointRepoName.value = selectedGrimoire.value?.name ?? fullName
    entryPointDialog.value = true
    return
  }
  try {
    const session = await api.createProjectSession(projectId)
    trackCodeRun(selectedGrimoire.value?.type ?? 'unknown')
    sessionUrl.value = session.sessionUrl
    sessionPackages.value = session.pyodidePackages ?? []
    // #752: 魔導書（grimoire）再生は Work ではないので★の対象なし。
    currentPlayWork.value = null
    playDialog.value = true
  }
  catch (e: unknown) {
    console.error('play grimoire failed', e)
    if (showScreeningErrorIfApplicable(e)) {
      // AST 違反は magician の EventDialog で表示
      return
    }
    throw e
  }
}

// Add / Delete User Grimoire
const onAddUserGrimoire = async () => {
  await triggerEventAndProcess(`CLICK:createNewGrimoire`)
}

const deleteDialog = ref(false)
const deleteProjectId = ref<string | null>(null)
const deleteProjectName = ref('')

const onDeleteUserGrimoire = (projectId: string) => {
  const item = allGrimoireItems.value.find(g => g.id === projectId)
  deleteProjectId.value = projectId
  deleteProjectName.value = item?.name ?? ''
  deleteDialog.value = true
}

const onConfirmDelete = async () => {
  if (!deleteProjectId.value) return
  try {
    await api.deleteProject(deleteProjectId.value)
    deleteDialog.value = false
    await loadProjects()
  }
  catch (e) {
    console.error('Delete failed', e)
  }
}

// Rename grimoire
const renameDialog = ref(false)
const renameProjectId = ref<string | null>(null)
const renameTitle = ref('')
const renameTitleError = ref('')

const onRenameGrimoire = (projectId: string) => {
  const item = allGrimoireItems.value.find(g => g.id === projectId)
  renameProjectId.value = projectId
  renameTitle.value = item?.name ?? ''
  renameTitleError.value = ''
  renameDialog.value = true
}

const validateRenameTitle = () => {
  const trimmed = renameTitle.value.trim()
  if (!trimmed) {
    renameTitleError.value = '名前を入力してください'
    return false
  }
  if (trimmed.length > 40) {
    renameTitleError.value = '40文字以内で入力してください'
    return false
  }
  renameTitleError.value = ''
  return true
}

const onConfirmRename = async () => {
  if (!renameProjectId.value || !validateRenameTitle()) return
  try {
    await api.renameProject(renameProjectId.value, renameTitle.value.trim())
    renameDialog.value = false
    await loadProjects()
  }
  catch (e) {
    console.error('Rename failed', e)
    renameTitleError.value = '名前の変更に失敗しました'
  }
}

// (chooseSubmitText は GrimoireSlot 内に固定ボタン化したため削除 #611)

// Lifecycle Hooks
const { finishLoading } = usePageReady()

const loadProjects = async () => {
  grimoireItems.value = (await api.listProjects('remix_source')).projects.map((item) => {
    return {
      id: item.projectId,
      name: item.name,
      type: item.type,
      readonly: item.readonly,
      description: item.description,
      createdAt: item.createdAt,
      updatedAt: item.updatedAt,
    }
  })
}

onMounted(async () => {
  await Promise.all([loadProjects(), loadMyWorks(), checkGithubConnection()])
  await loadGithubRepos()
  await finishLoading()

  const extra: string[] = []
  if (githubConnected.value) extra.push('github')
  if (userInfo.value?.privileges.includes('snapshot')) extra.push('snapshot')
  if (useRuntimeConfig().public.environment === 'local') extra.push('encodia_reference')
  if (extra.length) {
    categoriesToShow.value = [...categoriesToShow.value, ...extra]
  }
})

onBeforeUnmount(() => {
  // Resolve any pending entry-point Promise to prevent hanging
  if (crystallizeEntryPointResolve) {
    crystallizeEntryPointResolve(null)
    crystallizeEntryPointResolve = null
  }
})

// Dialog Store (used by crystallize and remix source flows)
const dialogStore = useDialogStore()

// Crystallize
// 子アカウントは保護者が「公開」を ON にしたときだけ（#975。canCrystallize はサーバが決める）
const canCrystallize = computed(() =>
  (userInfo.value?.privileges.includes('crystallize') ?? false) && userInfo.value?.canCrystallize !== false,
)
const crystallizeProjectId = ref<string | null>(null)
// #787: クリスタル化の API 待ち。true のあいだダイアログは閉じず工程を表示する。
const crystallizing = ref(false)

// #681: クリスタル化後に Pyxel 公式 User Examples への投稿を促すダイアログ。
// ダイアログ本体は centering43 レイアウトにマウントされ、状態は store が持つ
// （魔法研究会へ遷移しても消えないようにするため）。
const pyxelSubmissionStore = usePyxelSubmissionStore()
// #666: preview セッション作成時にユーザーが選んだ entrypoint を保持し、
// CrystallizeDialog (= 公開時の LLM 審査) に同じ entrypoint を渡す。
// 単一 .py プロジェクトの場合は null のままで backend が fallback する。
const crystallizeEntrypoint = ref<string | null>(null)
const previewDialog = ref(false)
const previewSessionUrl = ref('')
// #663: Pyodide built-in packages from the crystallize preview session.
const previewSessionPackages = ref<string[]>([])

// AST 審査 NG 時は magician speaker の EventDialog で表示 (#559)
const { tryShow: showScreeningErrorIfApplicable } = useScreeningError()

interface ThumbnailOption {
  dataUrl: string
  // #707: OGP still (PNG data URL) paired with this GIF thumbnail.
  stillDataUrl?: string
}
const capturedThumbnails = ref<ThumbnailOption[]>([])

// Crystallize entry point dialog (shared for GitHub and regular projects)
const crystallizeEntryPointDialog = ref(false)
const crystallizeEntryPointFiles = ref<{ path: string, size?: number }[]>([])
const crystallizeEntryPointTitle = ref('')
// #764: 取り込み前に見せる合計サイズ（GitHub 経路のみ。ゆりかごはビルド後に出す）
const crystallizeEntryPointSize = ref<number | null>(null)
const crystallizeEntryPointTruncated = ref(false)
// #764: プレビュー用ビルドで確定した pyxapp のサイズ
const previewSizeBytes = ref<number | null>(null)
let crystallizeEntryPointResolve: ((value: string | null) => void) | null = null

const showCrystallizeEntryPoint = (
  files: { path: string, size?: number }[],
  title: string,
  totalSizeBytes: number | null = null,
  truncated = false,
) => {
  return new Promise<string | null>((resolve) => {
    crystallizeEntryPointFiles.value = files
    crystallizeEntryPointTitle.value = title
    crystallizeEntryPointSize.value = totalSizeBytes
    crystallizeEntryPointTruncated.value = truncated
    crystallizeEntryPointResolve = resolve
    crystallizeEntryPointDialog.value = true
  })
}

const onCrystallizeEntryPointSelect = (path: string) => {
  crystallizeEntryPointDialog.value = false
  crystallizeEntryPointResolve?.(path)
  crystallizeEntryPointResolve = null
}

const onCrystallizeEntryPointCancel = () => {
  crystallizeEntryPointDialog.value = false
  crystallizeEntryPointResolve?.(null)
  crystallizeEntryPointResolve = null
}

const onCrystallize = async (projectId: string) => {
  if (projectId.startsWith('github:')) {
    // GitHub: MP check first, then entry point selection in the preview watch
    const fullName = projectId.replace('github:', '')
    entryPointRepoFullName.value = fullName
    entryPointRepoName.value = allGrimoireItems.value.find(i => i.id === projectId)?.name ?? fullName
    await triggerEventAndProcess('CLICK:crystallizeGithub')
    return
  }

  crystallizeProjectId.value = projectId
  await triggerEventAndProcess('CLICK:crystallize')
}

// Resolve entry point: if multiple .py files → show dialog, if one → auto-select, if zero → error
const resolveEntryPoint = async (
  pyFiles: { path: string, size?: number }[],
  title: string,
  totalSizeBytes: number | null = null,
  truncated = false,
): Promise<string | null> => {
  if (pyFiles.length > 1) {
    return await showCrystallizeEntryPoint(pyFiles, title, totalSizeBytes, truncated)
  }
  if (pyFiles.length === 1) return pyFiles[0]!.path
  alert('.py ファイルが見つかりません')
  return null
}

// Watch for the crystallizePreview dialog being opened by the event system
watch(() => dialogStore.crystallizePreview.visible, async (visible) => {
  if (!visible) return

  try {
    if (entryPointRepoFullName.value) {
      // GitHub flow: entry point → screening → preview
      const tree = await api.getGithubRepoTree(
        ...entryPointRepoFullName.value.split('/') as [string, string],
      )
      const entrypoint = await resolveEntryPoint(
        tree.files.map(f => ({ path: f.path, size: f.size ?? undefined })),
        entryPointRepoName.value || entryPointRepoFullName.value,
        // #764: Trees API が blob ごとの size を返すので、重い zipball を落とす
        // 前に「この作品の大きさ」を出せる。
        tree.totalSizeBytes ?? null,
        tree.truncated ?? false,
      )
      if (!entrypoint) {
        dialogStore.closeDialog('crystallizePreview', 'cancel')
        return
      }
      const [owner, repo] = entryPointRepoFullName.value.split('/')
      githubPreviewSource.value = { owner: owner ?? '', repo: repo ?? '', entrypoint }
      githubCompiling.value = true
      const session = await api.createGithubSession(owner ?? '', repo ?? '', entrypoint, true)
      githubCompiling.value = false
      githubPreviewSessionId.value = session.sessionId
      previewSessionUrl.value = session.sessionUrl
      previewSessionPackages.value = session.pyodidePackages ?? []
      previewSizeBytes.value = session.sizeBytes ?? null
      // #666: GitHub flow は project_id を持たない (crystallize 時に
      // create_work が直接 github_source を扱う) ので entrypoint は不要。
      crystallizeEntrypoint.value = null
    }
    else if (crystallizeProjectId.value) {
      // Regular flow: entry point → screening → preview
      const project = await api.getProject(crystallizeProjectId.value)
      const pyFiles = project.files.filter(f => f.name.endsWith('.py'))
      const entrypoint = pyFiles.length > 1
        ? await resolveEntryPoint(pyFiles.map(f => ({ path: f.name })), project.title)
        : undefined
      if (pyFiles.length > 1 && !entrypoint) {
        dialogStore.closeDialog('crystallizePreview', 'cancel')
        return
      }
      const session = await api.createProjectSession(crystallizeProjectId.value, entrypoint ?? undefined, true)
      previewSessionUrl.value = session.sessionUrl
      previewSessionPackages.value = session.pyodidePackages ?? []
      previewSizeBytes.value = session.sizeBytes ?? null
      // #666: 公開時 (CrystallizeDialog → generateSlug → LLM 審査) でも
      // preview と同じ entrypoint を使うため保持する。単一 .py の場合は null。
      crystallizeEntrypoint.value = entrypoint ?? null
    }
    else {
      dialogStore.closeDialog('crystallizePreview', 'cancel')
      return
    }
    previewDialog.value = true
  }
  catch (e: unknown) {
    githubCompiling.value = false
    console.error('crystallize preview failed', e)
    dialogStore.closeDialog('crystallizePreview', 'cancel')
    if (showScreeningErrorIfApplicable(e)) {
      // magician の EventDialog で表示済み
      return
    }
    // #765: `e.message` をそのまま出すと `FetchError: [POST] ".../session": 502`
    // が見えてしまう。原因と次の一手が分かる日本語に寄せる。
    alert(toUserMessage(e, 'プレビューを開けませんでした。少し時間をおいてお試しください。'))
  }
})

// Called when preview is done — close event dialog and pass thumbnails
// Both regular and GitHub flows now go through the event system,
// so closing the event dialog proceeds to OPEN:CrystallizeDialog step.
const onPreviewDone = (payload: { clips: Array<{ dataUrl: string, timestamp: number, stillDataUrl?: string }> }) => {
  capturedThumbnails.value = payload.clips.map(c => ({
    dataUrl: c.dataUrl,
    stillDataUrl: c.stillDataUrl,
  }))
  dialogStore.closeDialog('crystallizePreview', 'submit')
}

const onPreviewCancel = () => {
  capturedThumbnails.value = []
  previewSizeBytes.value = null
  githubPreviewSource.value = null
  entryPointRepoFullName.value = ''
  dialogStore.closeDialog('crystallizePreview', 'cancel')
}

const onSubmitCrystallize = async (formData: { title: string, description?: string, slug: string, tags: string[], remixable: boolean, gamepadCompatible: boolean, thumbnailData?: string, ogpImageData?: string }) => {
  // #787: ここでダイアログを閉じない。閉じてしまうと (a) 完了まで画面に何も出ず、
  // (b) 失敗したとき CrystallizeDialog の watch が入力を全部リセットするので
  // 撮影からやり直しになる。成功したときだけ閉じる。
  crystallizing.value = true

  // #791 MR7 hotfix: github 経路も SFN 非同期化されたので、triggerEvent は
  // task_id 受付だけで即返る。以前は sync で 30-60s 待つ間の overlay として
  // githubCompiling を立てていたが、いまはその待機は client polling が拾い、
  // 儀式ダイアログの label 遷移がそのまま UI feedback になる。ここで立てると
  // むしろ label 遷移を隠してしまうので触らない。preview 時の
  // createGithubSession (line 652 付近) の待機表示としては引き続き使う。

  try {
    // #762: 表紙アニメと OGP 静止画は **リクエスト body に載せない**。base64 の
    // まま送ると Lambda 同期呼び出しの payload 上限 (実効 6MB) を超えて 413 に
    // なる。カバー GIF の大きさは Pyxel の画面解像度 × キャプチャ秒数で決まる
    // ので、作品データを削っても直らない。先に S3 へ直接上げ、ここではキーだけ
    // を渡す。
    const { thumbnailData, ogpImageData, ...rest } = formData

    // #955: アップロードに失敗したら**ここで止める**（catch で理由を見せ、ダイアログは
    // 開いたまま）。以前は #762 の過渡期用に body 経由へ落としていたが、SFN 経路は
    // body の画像を載せられない（256KB 上限）ため、**表紙も OGP も無い作品が
    // 「成功」で公開されていた**（prod の fire-magic-vfx-demo）。過渡期はもう
    // 終わっているので、フォールバックそのものを外した。
    const coverParams: CoverImageKeys = await api.uploadCoverImages(thumbnailData, ogpImageData)

    // Build params — githubSource or projectId depending on flow.
    // #667: preview で選んだ entrypoint も渡し、本番ビルドが正しいファイルを
    // 焼き込むようにする (マルチファイルプロジェクトで非 first .py を公開する
    // ケース用)。GitHub flow は github_source.entrypoint を別経路で持つので不要。
    const params = githubPreviewSource.value
      ? { githubSource: githubPreviewSource.value, ...rest, ...coverParams }
      : {
          projectId: crystallizeProjectId.value,
          ...(crystallizeEntrypoint.value ? { entrypoint: crystallizeEntrypoint.value } : {}),
          ...rest,
          ...coverParams,
        }

    // #787: API 応答と、その応答が開く完了ダイアログのあいだで、
    // クリスタル化ダイアログを閉じる。triggerEventAndProcess をそのまま使うと
    // 完了ダイアログが開いたあとに閉じることになり、一瞬 2 枚が重なる。
    //
    // #791 hotfix: sfn mode では API 応答は「task 受付」だけで実処理はまだ。
    // task step 検出時はダイアログを閉じずに polling を開始し、儀式ラベル遷移
    // (ceremonyLabel = eventSystemStore.taskLabel) をユーザーに見せ続ける。
    // terminal 検知の瞬間に onTaskTerminated で閉じ、その直後に follow-up
    // ダイアログが unshift されるので「一瞬 2 枚が重なる」も回避できる。
    const result = await api.triggerEvent('CLICK:submitCrystallize', params)
    trackCrystallize(formData.slug)
    const hasTaskStep = result.results.some(r => r.steps.some(s => s.type === 'task'))
    if (hasTaskStep) {
      await processEventsSequentially(result.results, {
        onTaskTerminated: () => {
          crystallizing.value = false
          dialogStore.closeDialog('crystallize', 'submit')
        },
      })
    }
    else {
      crystallizing.value = false
      dialogStore.closeDialog('crystallize', 'submit')
      await processEventsSequentially(result.results)
    }
  }
  catch (e) {
    // #765: 生の FetchError を握りつぶさず、原因と次の一手を伝える。
    // 413（body 上限）と 504（タイムアウト）は特に効く — どちらも「作品が
    // 大きい」と結びつけられやすく、生のまま見せると原因の推測を誤らせる。
    //
    // #787: ダイアログは開いたままにする。入力（タイトル・説明・タグ・同意）と
    // 撮影済みサムネイルが残るので、そのまま再試行できる。
    console.error('crystallize failed', e)
    crystallizing.value = false
    githubCompiling.value = false
    alert(toUserMessage(e, 'クリスタル化に失敗しました。もう一度お試しください。'))
    return
  }
  finally {
    crystallizing.value = false
    // 失敗しても「コンパイル中…」が回り続けたり、選択状態が残ったりしないようにする。
    githubCompiling.value = false
    githubPreviewSource.value = null
    githubPreviewSessionId.value = null
    entryPointRepoFullName.value = ''
    crystallizeProjectId.value = null
    crystallizeEntrypoint.value = null
    capturedThumbnails.value = []
    previewSizeBytes.value = null
    await Promise.all([reloadGameState(), loadMyWorks()])
  }

  // #681: クリスタル化が完了したら Pyxel 公式 User Examples への投稿を促す。
  // 適格性: original な user 作品のみ（remixable=false の GitHub クリスタルは
  // 他者著作のため対象外）。新規 Work は slug で特定する。
  const newWork = myWorks.value.find(w => w.slug === formData.slug)
  if (newWork && newWork.remixable) {
    pyxelSubmissionStore.open(newWork)
  }
}

// Event handlers for Remix Source Selection (#602: multi-source)
interface RemixSource {
  projectId: string
  fileNames: string[]
}

const onSubmitRemixSource = async (sources: RemixSource[]) => {
  console.log('onSubmitRemixSource', sources)
  dialogStore.closeDialog('remixSource', 'submit')

  await triggerEventAndProcess(`CLICK:submitNewGrimoire`, {
    sources: JSON.stringify(sources),
  })
  await loadProjects()
  trackGrimoireCreate('user')
  await reloadGameState()
}
</script>

<template>
  <NuxtLayout :name="isMobile ? 'mobile-nav' : 'centering43'">
    <SceneMap
      scene-id="cmp_cradle"
      back-to-text="冒険者のキャンプに戻る"
      :mobile="isMobile"
      :mobile-background="cradleMapBg"
    >
      <template #map>
        <v-card class="pa-0 ma-0 fill-height w-100">
          <v-img
            src="@/assets/images/map/AreaMap_CradleOfFeatherQuill.webp"
            width="100%"
            height="100%"
            lazy-src="@/assets/images/map/LazyAreaMap_CradleOfFeatherQuill.png"
            :transition="false"
            cover
            class="content-image"
          >
            <div class="content-overlay pa-0">
              <v-tabs
                v-model="tab"
                class="content-tab"
              >
                <v-tab value="crystal">
                  <v-icon>{{ mdiDiamondStone }}</v-icon>魔導の結晶
                </v-tab>
                <v-tab value="vault">
                  <v-icon>{{ mdiBookshelf }}</v-icon>魔導の書庫
                </v-tab>
                <!-- 冒険者のクリスタル (#708): 運営編集スタッフ (Dr.D) のみ表示 -->
                <v-tab
                  v-if="isEditorial"
                  value="adventurers"
                >
                  <v-icon>{{ mdiAccountGroup }}</v-icon>冒険者のクリスタル
                </v-tab>
              </v-tabs>

              <v-card-text class="content-area">
                <v-tabs-window v-model="tab">
                  <v-tabs-window-item value="crystal">
                    <div class="crystal-scroll-area">
                      <div
                        v-if="myWorks.length === 0"
                        class="crystal-empty"
                      >
                        <div>まだクリスタル化した作品がありません</div>
                        <div class="crystal-empty-hint">
                          魔導の書庫から魔導書を選んで「クリスタル化」すると、<br>魔法研究会に作品を公開できます
                        </div>
                      </div>
                      <div
                        v-else
                        class="crystal-grid"
                      >
                        <div
                          v-for="work in myWorks"
                          :key="work.workId"
                          class="crystal-card-wrapper"
                          :class="{ 'crystal-private': work.visibility !== 'public' }"
                        >
                          <WorkCard
                            :work="work"
                            @play="onPlayWork(work)"
                          />
                          <v-btn
                            class="crystal-visibility-btn"
                            size="small"
                            color="primary"
                            variant="flat"
                            :prepend-icon="work.visibility === 'public' ? mdiEyeOff : mdiEye"
                            @click="onToggleVisibility(work)"
                          >
                            {{ work.visibility === 'public' ? '非公開にする' : '公開する' }}
                          </v-btn>
                        </div>
                      </div>
                      <!-- #803: 追記スクロール。`.crystal-scroll-area` が
                           `overflow-y: auto` の器だが、IntersectionObserver は
                           `root: null` でも祖先の overflow によるクリップを見るので
                           そのまま発火する。 -->
                      <WorkGalleryLoader
                        :has-more="myWorksHasMore"
                        :loading="myWorksLoadingMore"
                        :failed="Boolean(myWorksError)"
                        @load="loadMoreMyWorks"
                        @retry="retryMyWorks"
                      />
                    </div>
                  </v-tabs-window-item>

                  <v-tabs-window-item value="vault">
                    <div class="scroll-area">
                      <!-- GrimoireSelector は内部の v-checkbox 群と非同期 state
                         (githubAvailable / githubConnected / categoriesToShow の
                         onMounted 後の更新) によって Vuetify 内部の ID カウンタが
                         SSR と CSR でズレ、hydration の警告が出る。認証ガード
                         配下なので SSR の必要性も薄いため ClientOnly で囲う。 -->
                      <ClientOnly>
                        <CradleGrimoireSelector
                          :items="allGrimoireItems"
                          :categories="categoriesToShow"
                          :crystallize-types="canCrystallize ? ['user', 'encodia', 'github'] : (githubConnected ? ['github'] : [])"
                          :github-available="githubAvailable"
                          :github-connected="githubConnected"
                          :github-username="githubUsername"
                          @add="onAddUserGrimoire"
                          @select="onSelectGrimoire"
                          @delete="onDeleteUserGrimoire"
                          @program="onProgramGrimoire"
                          @read="onReadGrimoire"
                          @play="onPlayGrimoire"
                          @detail="onDetailGrimoire"
                          @crystallize="onCrystallize"
                          @rename="onRenameGrimoire"
                          @github-connect="onGithubConnect"
                          @github-disconnect="onGithubDisconnect"
                        />
                      </ClientOnly>
                    </div>
                  </v-tabs-window-item>

                  <!-- 冒険者のクリスタル (#708): 全冒険者の公開クリスタルを
                     ReadOnly で横断閲覧。各カードは「コードを読む」「動かして
                     みる」のみ (readonly なので編集・改名・削除は出ない)。 -->
                  <v-tabs-window-item
                    v-if="isEditorial"
                    value="adventurers"
                  >
                    <div class="adventurer-tab">
                      <div class="adventurer-hint">
                        全冒険者の公開クリスタルです。寸評を書くときのコード確認用に、読み取り専用で開けます。
                      </div>
                      <div
                        v-if="adventurerCrystalsLoaded && adventurerCrystals.length === 0"
                        class="crystal-empty"
                      >
                        <div>公開されたクリスタルがまだありません</div>
                      </div>
                      <!-- open 調停は GrimoireCardGrid が所有（#708 の配線もれ再発防止, #709） -->
                      <CradleGrimoireCardGrid
                        v-else
                        :items="adventurerCrystals"
                        @select="onSelectGrimoire"
                        @read="onReadGrimoire"
                        @play="onPlayGrimoire"
                        @detail="onDetailGrimoire"
                      />
                    </div>
                  </v-tabs-window-item>
                </v-tabs-window>
              </v-card-text>
            </div>
          </v-img>
        </v-card>

        <CradleMultiRemixDialog
          :items="allGrimoireItems"
          @submit="onSubmitRemixSource"
        />

        <CradleCrystallizePreviewDialog
          v-if="previewDialog"
          v-model="previewDialog"
          :session-url="previewSessionUrl"
          :pyodide-packages="previewSessionPackages"
          :size-bytes="previewSizeBytes"
          @done="onPreviewDone"
          @cancel="onPreviewCancel"
        />

        <CradleCrystallizeDialog
          :thumbnails="capturedThumbnails"
          :initial-title="githubPreviewSource ? githubPreviewSource.repo : (grimoireItems.find(g => g.id === crystallizeProjectId)?.name ?? '')"
          :project-id="githubPreviewSource ? undefined : (crystallizeProjectId ?? undefined)"
          :entrypoint="crystallizeEntrypoint ?? undefined"
          :is-github="!!githubPreviewSource"
          :submitting="crystallizing"
          @submit="onSubmitCrystallize($event)"
        />

        <Dialog
          v-model="detailDialog"
          title="くわしく見る"
          :submit-button="{ title: '選択する' }"
          :cancel-button="{ title: '閉じる' }"
          @submit="onOpenSelectedGrimoire()"
          @cancel="detailDialog = false"
        >
          <div class="detail-grimoire-body">
            <p class="detail-grimoire-name">
              {{ selectedGrimoire?.name ?? 'タイトル不明' }}
            </p>
            <MDC :value="selectedGrimoire?.description ?? '説明文なし'" />

            <!-- #743: 隠しリネージュビューア入口。ラベル・アイコンなしの極小ドット
               （見つけたユーザー向けの隠し機能。位置は固定なので知っている人は開ける）。 -->
            <button
              type="button"
              class="hidden-lineage-dot"
              aria-hidden="true"
              tabindex="-1"
              @click="lineageViewDialog = true"
            />
          </div>
        </Dialog>

        <DialogLineageViewDialog
          v-model="lineageViewDialog"
          :project-id="selectedGrimoire?.id ?? ''"
        />

        <Dialog
          v-model="renameDialog"
          title="魔導書の名前を変える"
          :submit-button="{ title: '変更する', disabled: !renameTitle.trim() || renameTitle.trim().length > 40 }"
          :cancel-button="{ title: 'やめる' }"
          @submit="onConfirmRename"
          @cancel="renameDialog = false"
        >
          <v-text-field
            v-model="renameTitle"
            label="新しい名前"
            maxlength="40"
            class="rename-input"
            counter
            variant="outlined"
            :error-messages="renameTitleError"
            autofocus
            @keydown.enter="onConfirmRename"
            @input="validateRenameTitle"
          />
        </Dialog>

        <Dialog
          v-model="deleteDialog"
          title="魔導書を削除する"
          :submit-button="{ title: '削除する', color: 'red' }"
          :cancel-button="{ title: 'やめる' }"
          @submit="onConfirmDelete"
          @cancel="deleteDialog = false"
        >
          <p>「{{ deleteProjectName }}」を削除しますか？</p>
        </Dialog>

        <DialogPlayDialog
          v-if="playDialog"
          v-model="playDialog"
          :session-url="sessionUrl"
          :pyodide-packages="sessionPackages"
          :work="currentPlayWork"
          :gamepad-compatible="currentPlayWork?.gamepadCompatible ?? false"
          :allow-fullscreen="!isMobile"
          show-gamepad-emulator
          back-text="リストに戻る"
          @back="playDialog = false"
        />

        <Dialog
          v-model="visibilityConfirmDialog"
          :title="visibilityTargetValue === 'private' ? '非公開にしますか？' : '公開しますか？'"
          :submit-button="{ title: visibilityTargetValue === 'private' ? '非公開にする' : '公開する' }"
          :cancel-button="{ title: 'キャンセル' }"
          @submit="onConfirmVisibility"
          @cancel="visibilityConfirmDialog = false"
        >
          <template v-if="visibilityTargetValue === 'private'">
            非公開にすると、魔法研究所やトップページから表示されなくなり、他のユーザーは遊べなくなります。
            <br>プレイ数は保存されます。
            <br><br>「{{ visibilityTargetWork?.title }}」を非公開にしますか？
          </template>
          <template v-else>
            公開すると、魔法研究所やトップページに表示され、他のユーザーが遊べるようになります。
            <br><br>「{{ visibilityTargetWork?.title }}」を公開しますか？
          </template>
        </Dialog>

        <!-- GitHub README -->
        <CradleGithubReadmeDialog
          v-model="readmeDialog"
          :repo-full-name="readmeRepoFullName"
          :repo-name="readmeRepoName"
        />

        <!-- GitHub compiling overlay -->
        <v-overlay
          :model-value="githubCompiling"
          persistent
          class="align-center justify-center"
          scrim="black"
          :opacity="0.7"
        >
          <div class="d-flex flex-column align-center">
            <v-progress-circular
              color="white"
              size="64"
              indeterminate
            />
            <div
              class="text-white mt-4"
              style="font-size: 1.2rem;"
            >
              コンパイル中…
            </div>
          </div>
        </v-overlay>

        <!-- GitHub entry point selection (play only) -->
        <CradleGithubEntryPointDialog
          v-model="entryPointDialog"
          :repo-full-name="entryPointRepoFullName"
          :repo-name="entryPointRepoName"
          @select="onEntryPointSelected"
        />

        <!-- Crystallize entry point selection (shared for GitHub and regular) -->
        <CradleEntryPointDialog
          v-model="crystallizeEntryPointDialog"
          :title="crystallizeEntryPointTitle"
          :files="crystallizeEntryPointFiles"
          :total-size-bytes="crystallizeEntryPointSize"
          :size-truncated="crystallizeEntryPointTruncated"
          @select="onCrystallizeEntryPointSelect"
          @cancel="onCrystallizeEntryPointCancel"
        />

        <!-- GitHub error -->
        <Dialog
          v-if="githubErrorMessage"
          :model-value="true"
          title="うまくいかなかった"
          :cancel-button="{ title: 'わかった', sound: 'click' }"
          @cancel="githubErrorMessage = null"
        >
          {{ githubErrorMessage }}
        </Dialog>

        <!-- GitHub connect confirm -->
        <Dialog
          v-model="githubConnectConfirm"
          title="接続しますか？"
          :submit-button="{ title: '接続する', sound: 'submit' }"
          :cancel-button="{ title: 'やめておく' }"
          @submit="onConfirmGithubConnect"
          @cancel="githubConnectConfirm = false"
        >
          八本足の猫の影が映るこの黒い鏡の奥から、強い魔導エネルギーが感じられます。
          <br><br>「<ruby>GitHub<rt>ギットハブ</rt></ruby>」
          <br><br>あなたがこれが何かわかるなら、エンコーディアの世界と接続し、黒い魔導書を呼び出すことができるでしょう。
        </Dialog>

        <!-- GitHub disconnect confirm -->
        <Dialog
          v-model="githubDisconnectConfirm"
          title="GitHub 連携を解除しますか？"
          :submit-button="{ title: '解除する', sound: 'click' }"
          :cancel-button="{ title: 'キャンセル' }"
          @submit="onConfirmGithubDisconnect"
          @cancel="githubDisconnectConfirm = false"
        >
          {{ githubUsername }} との連携を解除します。再接続はいつでも可能です。
        </Dialog>
      </template>

      <!-- #719 観点D: モバイルは「読む・遊ぶ」のみ。結晶=プレイ / 書庫=read-only
         ソースビューア＋実行。クリスタル化・GitHub 連携・改名・削除など編集系は出さない。 -->
      <template #mobile>
        <div class="cradle-mobile">
          <div class="cradle-mobile__tabs">
            <button
              type="button"
              class="cradle-mobile__tab"
              :class="{ 'cradle-mobile__tab--active': tab === 'crystal' }"
              @click="tab = 'crystal'"
            >
              <v-icon :icon="mdiDiamondStone" />魔導の結晶
            </button>
            <button
              type="button"
              class="cradle-mobile__tab"
              :class="{ 'cradle-mobile__tab--active': tab === 'vault' }"
              @click="tab = 'vault'"
            >
              <v-icon :icon="mdiBookshelf" />魔導の書庫
            </button>
          </div>

          <!-- 魔導の結晶: 自分の公開作品 → プレイ -->
          <div
            v-if="tab === 'crystal'"
            class="cradle-mobile__list"
          >
            <p
              v-if="myWorks.length === 0"
              class="cradle-mobile__empty"
            >
              まだクリスタル化した作品がありません。
            </p>
            <MobileListCard
              v-for="work in myWorks"
              :key="work.workId"
              :image="work.coverImageUrl"
              :image-webp="work.coverWebpUrl"
              :title="work.title"
              :subtitle="plainDesc(work.description)"
              @click="onPlayWork(work)"
            >
              <template #meta>
                <span
                  class="cradle-mobile__vis-badge"
                  :class="work.visibility === 'public'
                    ? 'cradle-mobile__vis-badge--public'
                    : 'cradle-mobile__vis-badge--private'"
                >
                  {{ work.visibility === 'public' ? '公開中' : '非公開' }}
                </span>
                <!-- カードのタップ（プレイ）と分離するため stop する -->
                <button
                  type="button"
                  class="cradle-mobile__vis-toggle"
                  @click.stop="onToggleVisibility(work)"
                >
                  <v-icon
                    :icon="work.visibility === 'public' ? mdiEyeOff : mdiEye"
                    size="16"
                  />
                  {{ work.visibility === 'public' ? '非公開にする' : '公開する' }}
                </button>
              </template>
            </MobileListCard>
            <!-- #803: モバイルはウィンドウスクロールで追記する。 -->
            <WorkGalleryLoader
              :has-more="myWorksHasMore"
              :loading="myWorksLoadingMore"
              :failed="Boolean(myWorksError)"
              @load="loadMoreMyWorks"
              @retry="retryMyWorks"
            />
          </div>

          <!-- 魔導の書庫: 魔導書 → read-only ソースビューア（タップで開く） -->
          <div
            v-else
            class="cradle-mobile__list"
          >
            <!-- #721: タイプ別フィルタ（存在するタイプだけ出す） -->
            <div
              v-if="vaultTypeOptions.length > 1"
              class="cradle-mobile__filter"
            >
              <button
                type="button"
                class="cradle-mobile__chip"
                :class="{ 'cradle-mobile__chip--active': vaultFilter === 'all' }"
                @click="vaultFilter = 'all'"
              >
                すべて
              </button>
              <button
                v-for="t in vaultTypeOptions"
                :key="t"
                type="button"
                class="cradle-mobile__chip"
                :class="{ 'cradle-mobile__chip--active': vaultFilter === t }"
                @click="vaultFilter = t"
              >
                {{ GRIMOIRE_TYPE_LABEL[t] }}
              </button>
            </div>

            <p
              v-if="filteredGrimoireItems.length === 0"
              class="cradle-mobile__empty"
            >
              まだ魔導書がありません。
            </p>
            <MobileListCard
              v-for="item in filteredGrimoireItems"
              :key="item.id"
              :image="grimoireCover(item.type)"
              image-fit="contain"
              :title="item.name"
              :subtitle="plainDesc(item.description)"
              @click="onOpenMobileGrimoire(item)"
            />
          </div>
        </div>

        <!-- read-only ソースビューア -->
        <MobileSourceViewer
          v-model="mobileViewerOpen"
          :project-id="mobileViewerProjectId"
          :title="mobileViewerTitle"
          @run="onMobileViewerRun"
        />

        <!-- プレイ（結晶プレイ / 書庫 RUN 共通。モバイルは没入で開く） -->
        <DialogPlayDialog
          v-if="playDialog"
          v-model="playDialog"
          :session-url="sessionUrl"
          :pyodide-packages="sessionPackages"
          :work="currentPlayWork"
          :gamepad-compatible="currentPlayWork?.gamepadCompatible ?? false"
          show-gamepad-emulator
          back-text="リストに戻る"
          :fixed-fullscreen="isMobile"
          @back="playDialog = false"
        />

        <!-- #721: クリスタルの公開/非公開 確認（desktop と同じフローを流用）。
             SceneMap のスロットは v-if/v-else 排他なので desktop 側とは同時に出ない。 -->
        <Dialog
          v-model="visibilityConfirmDialog"
          :title="visibilityTargetValue === 'private' ? '非公開にしますか？' : '公開しますか？'"
          :submit-button="{ title: visibilityTargetValue === 'private' ? '非公開にする' : '公開する' }"
          :cancel-button="{ title: 'キャンセル' }"
          @submit="onConfirmVisibility"
          @cancel="visibilityConfirmDialog = false"
        >
          <template v-if="visibilityTargetValue === 'private'">
            非公開にすると、魔法研究所やトップページから表示されなくなり、他のユーザーは遊べなくなります。
            <br>プレイ数は保存されます。
            <br><br>「{{ visibilityTargetWork?.title }}」を非公開にしますか？
          </template>
          <template v-else>
            公開すると、魔法研究所やトップページに表示され、他のユーザーが遊べるようになります。
            <br><br>「{{ visibilityTargetWork?.title }}」を公開しますか？
          </template>
        </Dialog>
      </template>
    </SceneMap>
  </NuxtLayout>
</template>

<style scoped>
.content-image :deep(.v-responsive__sizer) {
  display: none !important;
}

.content-overlay {
  position: absolute;
  top: max(40px, calc(5.625 * var(--u)));
  left: 0;
  width: 100%;
  bottom: 10%;
  z-index: 5;

  display: flex;
  flex-direction: column;
}

.content-tab {
  margin-left: 1%;
  flex: 0 0 auto;
  --v-tabs-height: max(28px, calc(3.75 * var(--u)));
}

.content-tab :deep(.v-tab) {
  font-family: "Noto Sans JP", sans-serif;
  font-weight: bold;
  background-color: rgb(196, 96, 96, 0.7);
  height: max(28px, calc(3.75 * var(--u)));
  min-height: 0;
  font-size: max(10px, calc(1.25 * var(--u)));

  text-shadow:
    1px 0 white,
    -1px 0 white,
    0 1px white,
    0 -1px white,
    1px 1px white,
    -1px -1px white,
    1px -1px white,
    -1px 1px white;
}

.content-area {
  padding: 0;

  background-color: rgb(128, 64, 64, 0.5);

  flex: 1 1 auto;
  min-height: 0;
  overflow: hidden;
}

.scroll-area {
  height: 100%;
}

.crystal-scroll-area {
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
}

/* #670 Phase 1: WorkCard grid を auto-fill minmax(280px, 1fr) で
   揃える。各セル直下に「公開する/非公開にする」トグルを抱えるため
   align-items: start で行ごとの高さズレを他の行に伝播させない。
   Phase 3 (#670 後半): 2K 以上で 6 列に達すると、card 内部の
   `--u` ベース (= centering43 幅の 1%) フォント/パディングが card 幅
   に対して大きすぎて崩れる。列数上限を 5 に抑えるため
   `max(280px, 5 列均等割 fair-share)` を minmax 下限にする。 */
.crystal-grid {
  display: grid;
  grid-template-columns: repeat(
    auto-fill,
    minmax(max(280px, calc((100% - 4 * 12px) / 5)), 1fr)
  );
  align-items: start;
  gap: 12px;
  padding: 4px;
}

.crystal-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: max(8px, calc(1 * var(--u)));
  height: 100%;
  color: #ECEFF1;
  font-size: max(12px, calc(1.5 * var(--u)));
  opacity: 0.7;
}

/* 冒険者のクリスタル (#708/#709): ヒント行 + GrimoireCardGrid（グリッドは
   コンテナが自前でスクロール）。書庫タブと同じ縦フレックス構成。 */
.adventurer-tab {
  height: 100%;
  display: flex;
  flex-direction: column;
}

.adventurer-hint {
  flex: 0 0 auto;
  color: #ECEFF1;
  font-size: max(10px, calc(1.2 * var(--u)));
  line-height: 1.6;
  padding: max(6px, calc(0.75 * var(--u))) max(8px, calc(1 * var(--u)));
  opacity: 0.85;
}

.crystal-empty-hint {
  font-size: max(10px, calc(1.2 * var(--u)));
  color: #B0BEC5;
  text-align: center;
  line-height: 1.6;
}

.crystal-card-wrapper {
  position: relative;
  min-width: 0;
  overflow: hidden;
}

.crystal-private :deep(.work-card) {
  opacity: 0.5;
}

.crystal-visibility-btn {
  width: 100%;
  margin-top: max(2px, calc(0.25 * var(--u)));
  font-family: "Noto Sans JP", sans-serif;
}

.detail-grimoire-body {
  position: relative;
  min-height: 100%;
}

.detail-grimoire-name {
  font-size: max(15px, calc(2.1 * var(--u)));
  font-weight: bold;
  color: #333;
  word-break: break-all;
  margin-bottom: max(4px, calc(0.5 * var(--u)));
}

/* #743: 隠しリネージュビューアの入口。見た目と同じ 8×8 の極小ドット＝当たり判定も
   8×8 に絞る（気づかれにくさ優先）。ラベル・ツールチップ・アイコンは意図的に付けない。 */
.hidden-lineage-dot {
  position: absolute;
  right: 10px;
  top: 10px;
  width: 8px;
  height: 8px;
  padding: 0;
  margin: 0;
  border: none;
  border-radius: 50%;
  background: #000;
  opacity: 0.15;
  cursor: pointer;
}

/* v-tabs-window チェーンを通して高さを伝播させる */
.content-area :deep(.v-tabs-window),
.content-area :deep(.v-window__container),
.content-area :deep(.v-tabs-window-item) {
  height: 100%;
}

.rename-input :deep(input) {
  font-size: 150%;
}

/* #719 観点D: モバイルのゆりかご（mobile-nav レイアウト内・dvw/dvh 可）。 */
.cradle-mobile {
  padding: 4px 10px 16px;
  font-family: "Noto Sans JP", sans-serif;
}

.cradle-mobile__tabs {
  display: flex;
  gap: 8px;
  padding: 4px 0 10px;
}

.cradle-mobile__tab {
  flex: 1 1 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 8px 6px;
  border: 1px solid rgba(255, 255, 255, 0.4);
  border-radius: 10px;
  background: rgba(60, 40, 40, 0.55);
  color: #fff;
  font-weight: 700;
  font-size: 0.95rem;
  cursor: pointer;
}

.cradle-mobile__tab--active {
  background: rgba(196, 96, 96, 0.85);
  border-color: #fff;
}

.cradle-mobile__list {
  display: flex;
  flex-direction: column;
  gap: 12px;
  max-width: 560px;
  margin: 0 auto;
}

.cradle-mobile__empty {
  text-align: center;
  color: #ECEFF1;
  text-shadow: #000 1px 1px 3px;
  padding: 28px 12px;
  font-size: 0.95rem;
}

/* #721: 書庫のタイプ別フィルタチップ */
.cradle-mobile__filter {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 4px;
}

.cradle-mobile__chip {
  font-family: "Noto Sans JP", sans-serif;
  font-size: 0.82rem;
  font-weight: 700;
  color: #f3e6cf;
  background: rgba(0, 0, 0, 0.32);
  border: 1px solid rgba(255, 255, 255, 0.35);
  border-radius: 999px;
  padding: 5px 14px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}

.cradle-mobile__chip--active {
  color: #3a2417;
  background: #f3e6cf;
  border-color: #f3e6cf;
}

/* #721: クリスタルの公開状態バッジ + 公開/非公開トグル */
.cradle-mobile__vis-badge {
  font-size: 0.72rem;
  font-weight: 700;
  border-radius: 6px;
  padding: 2px 8px;
}

.cradle-mobile__vis-badge--public {
  color: #2e7d54;
  background: rgba(75, 191, 129, 0.16);
}

.cradle-mobile__vis-badge--private {
  color: #6b6b6b;
  background: rgba(120, 120, 120, 0.16);
}

.cradle-mobile__vis-toggle {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-family: "Noto Sans JP", sans-serif;
  font-size: 0.76rem;
  font-weight: 700;
  color: #9a3320;
  background: rgba(177, 18, 38, 0.08);
  border: 1px solid rgba(177, 18, 38, 0.28);
  border-radius: 8px;
  padding: 3px 10px;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
}
</style>
